import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SystemUpdateAltOutlinedIcon from "@mui/icons-material/SystemUpdateAltOutlined";
import AppleIcon from "@mui/icons-material/Apple";
import AndroidIcon from "@mui/icons-material/Android";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import AccessController from "_shared/AccessController";
import { DashGridL2 } from "layouts/dash-grid";
import TopBar from "_shared/Top";
import { Toaster } from "react-hot-toast";
import { toastMessage } from "utils/toastMessage";
import { PageHeader, SectionCard, EmptyState } from "_shared/ui";
import { getAppVersions, addAppVersion } from "_controllers/utilities";

// Validate a build number: a positive integer.
const isValidBuild = (value) => {
  if (value === "" || value === null || value === undefined) return false;
  const n = Number(value);
  return Number.isInteger(n) && n > 0;
};

const Main = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [revertOpen, setRevertOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);

  const [form, setForm] = useState({
    latestIOSBuildNumber: "",
    latestAndroidBuildNumber: "",
    forceIOS: false,
    forceAndroid: false,
    changeLogs: [""],
  });
  const [errors, setErrors] = useState({});

  const loadData = async () => {
    try {
      setLoading(true);
      const listRes = await getAppVersions();

      const list = Array.isArray(listRes?.appVersion) ? listRes.appVersion : [];
      // Newest first — the newest record is what the mobile apps read as current.
      list.sort(
        (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
      );
      setHistory(list);

      const latest = list[0] || null;
      setCurrent(latest);

      if (latest) {
        setForm({
          latestIOSBuildNumber: latest.latestIOSBuildNumber ?? "",
          latestAndroidBuildNumber: latest.latestAndroidBuildNumber ?? "",
          forceIOS: !!latest.forceIOS,
          forceAndroid: !!latest.forceAndroid,
          changeLogs:
            Array.isArray(latest.changeLogs) && latest.changeLogs.length > 0
              ? latest.changeLogs.map((c) => c?.description || "")
              : [""],
        });
      }
    } catch (err) {
      toastMessage(
        err?.response?.data?.error || "Failed to load app version",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const setField = (name) => (value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const setChangeLog = (index, value) => {
    setForm((prev) => {
      const next = [...prev.changeLogs];
      next[index] = value;
      return { ...prev, changeLogs: next };
    });
  };

  const addChangeLog = () =>
    setForm((prev) => ({ ...prev, changeLogs: [...prev.changeLogs, ""] }));

  const removeChangeLog = (index) =>
    setForm((prev) => {
      const next = prev.changeLogs.filter((_, i) => i !== index);
      return { ...prev, changeLogs: next.length ? next : [""] };
    });

  const validate = () => {
    const next = {};
    if (!isValidBuild(form.latestIOSBuildNumber))
      next.latestIOSBuildNumber = "Enter a positive whole number";
    if (!isValidBuild(form.latestAndroidBuildNumber))
      next.latestAndroidBuildNumber = "Enter a positive whole number";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmitClick = () => {
    if (!validate()) return;
    // Forcing an update logs users out of old builds — confirm first.
    if (form.forceIOS || form.forceAndroid) {
      setConfirmOpen(true);
      return;
    }
    submit();
  };

  const submit = async () => {
    setConfirmOpen(false);
    try {
      setSubmitting(true);
      const payload = {
        latestIOSBuildNumber: Number(form.latestIOSBuildNumber),
        latestAndroidBuildNumber: Number(form.latestAndroidBuildNumber),
        forceIOS: form.forceIOS,
        forceAndroid: form.forceAndroid,
        changeLogs: form.changeLogs
          .map((d) => d.trim())
          .filter(Boolean)
          .map((description) => ({ description })),
      };
      await addAppVersion(payload);
      toastMessage("New app version published", "success");
      await loadData();
    } catch (err) {
      toastMessage(
        err?.response?.data?.error || "Failed to publish app version",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  // One-click revert: republish the current build numbers with both force flags
  // off. The newer record wins, so the apps stop forcing the update.
  const revertForce = async () => {
    setRevertOpen(false);
    if (!current) return;
    try {
      setSubmitting(true);
      await addAppVersion({
        latestIOSBuildNumber: current.latestIOSBuildNumber,
        latestAndroidBuildNumber: current.latestAndroidBuildNumber,
        forceIOS: false,
        forceAndroid: false,
        changeLogs: Array.isArray(current.changeLogs)
          ? current.changeLogs
              .map((c) => ({ description: c?.description }))
              .filter((c) => c.description)
          : [],
      });
      toastMessage("Force update disabled", "success");
      await loadData();
    } catch (err) {
      toastMessage(
        err?.response?.data?.error || "Failed to disable force update",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Force Update"
        subtitle="Publish the minimum build numbers and force update for iOS & Android"
      />

      {/* Current version */}
      <SectionCard
        title="Current Version"
        sx={{ mb: 3 }}
        action={
          current && (current.forceIOS || current.forceAndroid) ? (
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => setRevertOpen(true)}
              disabled={submitting}
            >
              Disable force update
            </Button>
          ) : null
        }
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : current ? (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <AppleIcon />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    iOS build
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {current.latestIOSBuildNumber ?? "—"}
                  </Typography>
                </Box>
                <Chip
                  label={current.forceIOS ? "Forced" : "Optional"}
                  color={current.forceIOS ? "error" : "default"}
                  variant={current.forceIOS ? "filled" : "outlined"}
                  size="small"
                />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <AndroidIcon />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Android build
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {current.latestAndroidBuildNumber ?? "—"}
                  </Typography>
                </Box>
                <Chip
                  label={current.forceAndroid ? "Forced" : "Optional"}
                  color={current.forceAndroid ? "error" : "default"}
                  variant={current.forceAndroid ? "filled" : "outlined"}
                  size="small"
                />
              </Stack>
            </Grid>
            {Array.isArray(current.changeLogs) &&
              current.changeLogs.length > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ mb: 1.5 }} />
                  <Typography variant="caption" color="text.secondary">
                    Change logs
                  </Typography>
                  <ul style={{ margin: "4px 0 0", paddingLeft: 20 }}>
                    {current.changeLogs.map((c, i) => (
                      <li key={i}>
                        <Typography variant="body2">
                          {c?.description}
                        </Typography>
                      </li>
                    ))}
                  </ul>
                </Grid>
              )}
          </Grid>
        ) : (
          <EmptyState
            icon={SystemUpdateAltOutlinedIcon}
            title="No version published yet"
            description="Publish the first app version using the form below."
          />
        )}
      </SectionCard>

      {/* Publish */}
      <SectionCard title="Publish New Version" sx={{ mb: 3 }}>
        <Stack spacing={3}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                type="number"
                label="Latest iOS build number"
                value={form.latestIOSBuildNumber}
                onChange={(e) =>
                  setField("latestIOSBuildNumber")(e.target.value)
                }
                error={!!errors.latestIOSBuildNumber}
                helperText={errors.latestIOSBuildNumber}
                InputProps={{ inputProps: { min: 1, step: 1 } }}
                fullWidth
                required
              />
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Switch
                    checked={form.forceIOS}
                    onChange={(e) => setField("forceIOS")(e.target.checked)}
                    color="error"
                  />
                }
                label="Force update on iOS"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                type="number"
                label="Latest Android build number"
                value={form.latestAndroidBuildNumber}
                onChange={(e) =>
                  setField("latestAndroidBuildNumber")(e.target.value)
                }
                error={!!errors.latestAndroidBuildNumber}
                helperText={errors.latestAndroidBuildNumber}
                InputProps={{ inputProps: { min: 1, step: 1 } }}
                fullWidth
                required
              />
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Switch
                    checked={form.forceAndroid}
                    onChange={(e) => setField("forceAndroid")(e.target.checked)}
                    color="error"
                  />
                }
                label="Force update on Android"
              />
            </Grid>
          </Grid>

          <Box>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Change logs
              </Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addChangeLog}>
                Add
              </Button>
            </Stack>
            <Stack spacing={1}>
              {form.changeLogs.map((log, i) => (
                <Stack key={i} direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    placeholder={`Change #${i + 1}`}
                    value={log}
                    onChange={(e) => setChangeLog(i, e.target.value)}
                    fullWidth
                  />
                  <IconButton
                    aria-label="Remove change log"
                    onClick={() => removeChangeLog(i)}
                    size="small"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          </Box>

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              color="inherit"
              onClick={loadData}
              disabled={submitting}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitClick}
              disabled={submitting}
              startIcon={
                submitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SystemUpdateAltOutlinedIcon />
                )
              }
            >
              Publish Version
            </Button>
          </Stack>
        </Stack>
      </SectionCard>

      {/* History */}
      <SectionCard title="Version History" disablePadding>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : history.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align="center">iOS build</TableCell>
                  <TableCell align="center">Android build</TableCell>
                  <TableCell align="center">Force iOS</TableCell>
                  <TableCell align="center">Force Android</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((v) => (
                  <TableRow key={v._id} hover>
                    <TableCell>
                      {v.createdAt
                        ? new Date(v.createdAt).toLocaleString()
                        : "—"}
                    </TableCell>
                    <TableCell align="center">
                      {v.latestIOSBuildNumber ?? "—"}
                    </TableCell>
                    <TableCell align="center">
                      {v.latestAndroidBuildNumber ?? "—"}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={v.forceIOS ? "Yes" : "No"}
                        color={v.forceIOS ? "error" : "default"}
                        variant={v.forceIOS ? "filled" : "outlined"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={v.forceAndroid ? "Yes" : "No"}
                        color={v.forceAndroid ? "error" : "default"}
                        variant={v.forceAndroid ? "filled" : "outlined"}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ p: 2.5 }}>
            <EmptyState title="No version history" />
          </Box>
        )}
      </SectionCard>

      {/* Confirm force update */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Force update?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Users on older builds will be blocked until they update on{" "}
            <strong>
              {form.forceIOS && form.forceAndroid
                ? "iOS and Android"
                : form.forceIOS
                ? "iOS"
                : "Android"}
            </strong>
            . Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={submit}>
            Publish &amp; force
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm disabling force update (revert) */}
      <Dialog open={revertOpen} onClose={() => setRevertOpen(false)}>
        <DialogTitle>Disable force update?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This publishes a new version with the same build numbers but force
            update turned <strong>off</strong> for both platforms. Users will no
            longer be blocked. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setRevertOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={revertForce}>
            Disable force update
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const ForceUpdate = () => (
  <AccessController roles={["ADMIN"]}>
    <DashGridL2 Top={TopBar} Main={Main} />
    <Toaster />
  </AccessController>
);

export default ForceUpdate;
